/**
 * Local draft persistence for the e-sign template editor.
 *
 * Edits are kept in-memory until the user saves; a crash, refresh, or
 * accidental navigation would silently lose work. The editor autosaves a
 * draft snapshot to localStorage (keyed per template) so the work can be
 * restored on return. Drafts store the serialized stage data (`actions`),
 * which carries the resolution-independent `original*` coordinates — after a
 * restore, `adjustDataCoords()` re-derives display coordinates for the
 * current viewport, so drafts survive window-size changes.
 */

const DRAFT_STORAGE_PREFIX = 'fw-template-draft:';

export interface TemplateDraft {
    savedAt: string;
    actions: string;
}

function draftKey(templateUuid: string): string {
    return DRAFT_STORAGE_PREFIX + templateUuid;
}

export function saveTemplateDraft(templateUuid: string, actions: string): void {
    try {
        const draft: TemplateDraft = {
            savedAt: new Date().toISOString(),
            actions,
        };
        window.localStorage.setItem(draftKey(templateUuid), JSON.stringify(draft));
    } catch {
        // Storage full or unavailable — autosave silently degrades.
    }
}

export function loadTemplateDraft(templateUuid: string): TemplateDraft | null {
    try {
        const raw = window.localStorage.getItem(draftKey(templateUuid));
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as TemplateDraft;
        if (typeof parsed?.actions !== 'string' || typeof parsed?.savedAt !== 'string') {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

export function clearTemplateDraft(templateUuid: string): void {
    try {
        window.localStorage.removeItem(draftKey(templateUuid));
    } catch {
        // Nothing to clear.
    }
}

/**
 * A draft is worth offering to restore only when it differs from what the
 * server already has (otherwise it's just a stale copy of saved state).
 */
export function hasRestorableDraft(templateUuid: string, serverActions: string): boolean {
    const draft = loadTemplateDraft(templateUuid);
    return draft !== null && draft.actions !== serverActions;
}
